import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const WhitepaperPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ background: 'var(--bg-dark)', minHeight: '100vh', paddingTop: '80px' }}>
      <style>{`
        .whitepaper-content section {
          border-bottom: none !important;
          padding: 0 !important;
        }
        .whitepaper-content section::before {
          display: none !important;
        }
      `}</style>
      <Header />

      <div className="container whitepaper-content" style={{ maxWidth: '900px', padding: '3rem 1rem' }}>
        {/* Title Section */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 700,
            color: '#EFEFE3',
            marginBottom: '1.5rem',
            letterSpacing: '-0.02em'
          }}>
            Ocean Intelligence: From Genomics to Finance
          </h1>
          <p style={{
            fontSize: '1.3rem',
            color: '#0888D0',
            fontWeight: 500,
            marginBottom: '1rem'
          }}>
            Transforming Marine Conservation Through Advanced Analytics
          </p>
          <p style={{
            fontSize: '1rem',
            color: '#D5D6CC',
            fontStyle: 'italic'
          }}>
            Ocean BioMetrics Ltd. White Paper
          </p>
        </div>

        {/* Executive Summary */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{
            fontSize: '1.8rem',
            color: '#EFEFE3',
            marginBottom: '1.5rem',
            fontWeight: 600
          }}>
            Executive Summary
          </h2>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Ocean BioMetrics Ltd. represents a paradigm shift in marine ecosystem understanding and valuation. By integrating cutting-edge genomic technologies with artificial intelligence and financial innovation, we address the critical gap between ocean conservation needs and available funding mechanisms. Our comprehensive approach transforms complex biological data into standardized, actionable metrics that enable performance-based conservation finance at scale.
          </p>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8' }}>
            The oceans face unprecedented challenges: climate change, biodiversity loss, pollution, and overfishing threaten the very foundation of marine ecosystems and the $3 trillion annual blue economy. Traditional conservation approaches, limited by chronic underfunding and incomplete data, struggle to match the scale and urgency of these challenges. Ocean BioMetrics Ltd. bridges this gap by making ocean health measurable, conservation impact verifiable, and biodiversity outcomes investable.
          </p>
        </section>

        {/* The Challenge */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{
            fontSize: '1.8rem',
            color: '#EFEFE3',
            marginBottom: '1.5rem',
            fontWeight: 600
          }}>
            The Challenge: Invisible Oceans, Unmeasurable Value
          </h2>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            The Data Gap
          </h3>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Despite covering 71% of Earth's surface and containing 95% of habitable space, our oceans remain largely unmeasured and misunderstood. Traditional marine surveys capture less than 5% of ocean biodiversity—only the visible, charismatic species. This leaves 95% of marine life, including the microbial communities that drive ocean productivity and climate regulation, completely unaccounted for in conservation planning and valuation.
          </p>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Current monitoring methods are expensive, invasive, and limited in scope. Visual surveys require extensive ship time and diving operations, disturb sensitive habitats, and miss nocturnal, cryptic, and microscopic species. Satellite monitoring provides surface-level data but cannot penetrate the ocean's depths. Sensor networks offer point measurements but lack biological context. The result is a fragmented, incomplete picture of ocean health that fails to capture ecosystem complexity and function.
          </p>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            The Finance Gap
          </h3>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Marine conservation faces a $150 billion annual funding gap. While terrestrial conservation has developed mature markets for carbon credits and biodiversity offsets, ocean conservation lacks comparable mechanisms. The primary barriers are:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li><strong>Measurement challenges:</strong> Without standardized metrics for ocean health and biodiversity, it's impossible to quantify conservation impact or verify outcomes</li>
            <li><strong>Attribution problems:</strong> Ocean ecosystems are interconnected and dynamic, making it difficult to link specific actions to measurable results</li>
            <li><strong>Temporal mismatches:</strong> Marine ecosystem recovery operates on different timescales than financial instruments require</li>
            <li><strong>Regulatory uncertainty:</strong> Fragmented governance and unclear benefit-sharing frameworks deter investment</li>
          </ul>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            The Knowledge Gap
          </h3>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8' }}>
            Even when data exists, it remains siloed and inaccessible. Marine scientists, conservation managers, policymakers, and investors speak different languages and operate with incompatible frameworks. Complex ecological data cannot be easily translated into financial metrics. Conservation success stories cannot be verified against standardized benchmarks. Local communities lack access to the information needed to manage their resources sustainably.
          </p>
        </section>

        {/* Our Solution */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{
            fontSize: '1.8rem',
            color: '#EFEFE3',
            marginBottom: '1.5rem',
            fontWeight: 600
          }}>
            Our Solution: Making the Invisible Visible, the Complex Clear
          </h2>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Revolutionary Data Collection: Beyond Species Counts
          </h3>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Ocean BioMetrics Ltd. employs a tiered genomic approach that reveals the full spectrum of marine biodiversity:
          </p>

          <h4 style={{ fontSize: '1.2rem', color: '#EFEFE3', marginTop: '1.5rem', marginBottom: '0.8rem' }}>
            Environmental DNA (eDNA) Metabarcoding
          </h4>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Every organism leaves genetic traces in seawater through shed cells, mucus, and waste. By collecting water samples and sequencing specific genetic markers, we detect thousands of species from a single liter of seawater—from bacteria to blue whales. This non-invasive method provides comprehensive species inventories, relative abundance data, and biodiversity indices at a fraction of traditional survey costs.
          </p>

          <h4 style={{ fontSize: '1.2rem', color: '#EFEFE3', marginTop: '1.5rem', marginBottom: '0.8rem' }}>
            Metagenomics: The Functional Revolution
          </h4>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            While metabarcoding tells us who's there, metagenomics reveals what they're doing. By sequencing all genetic material in environmental samples, we identify:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li><strong>Metabolic pathways:</strong> How organisms process nutrients, produce energy, and cycle elements</li>
            <li><strong>Ecosystem functions:</strong> Primary production, nutrient cycling, carbon sequestration capacity</li>
            <li><strong>Stress responses:</strong> Indicators of pollution, temperature stress, acidification impacts</li>
            <li><strong>Biosynthetic potential:</strong> Novel compounds, enzymes, and genetic resources</li>
            <li><strong>Community interactions:</strong> Symbioses, predation patterns, disease dynamics</li>
          </ul>

          <h4 style={{ fontSize: '1.2rem', color: '#EFEFE3', marginTop: '1.5rem', marginBottom: '0.8rem' }}>
            Multi-Omics Integration
          </h4>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            We combine genomic data with transcriptomics (gene expression), proteomics (protein production), and metabolomics (chemical processes) to create dynamic models of ecosystem function. This multi-layered approach reveals not just the blueprint of life, but how that blueprint responds to environmental change in real-time.
          </p>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Advanced Analytics: From Data to Intelligence
          </h3>

          <h4 style={{ fontSize: '1.2rem', color: '#EFEFE3', marginTop: '1.5rem', marginBottom: '0.8rem' }}>
            AI-Powered Pattern Recognition
          </h4>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Our machine learning models process billions of genetic sequences, identifying patterns invisible to traditional analysis. Deep learning algorithms trained on global ocean datasets can:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li>Predict ecosystem shifts before visible symptoms appear</li>
            <li>Identify keystone species and critical ecological relationships</li>
            <li>Detect invasive species at early colonization stages</li>
            <li>Quantify ecosystem resilience and recovery potential</li>
            <li>Map functional diversity hotspots and conservation priorities</li>
          </ul>

          <h4 style={{ fontSize: '1.2rem', color: '#EFEFE3', marginTop: '1.5rem', marginBottom: '0.8rem' }}>
            Knowledge Graph Architecture
          </h4>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            We structure ocean data as interconnected knowledge graphs, capturing relationships between species, functions, environments, and human activities. This enables:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li>Natural language queries across complex datasets</li>
            <li>Automated hypothesis generation and testing</li>
            <li>Predictive modeling of intervention outcomes</li>
            <li>Cross-scale analysis from genes to ecosystems</li>
            <li>Integration with satellite, sensor, and socioeconomic data</li>
          </ul>

          <h4 style={{ fontSize: '1.2rem', color: '#EFEFE3', marginTop: '1.5rem', marginBottom: '0.8rem' }}>
            Standardized Metrics and Benchmarks
          </h4>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            We translate complex ecological data into standardized metrics aligned with international frameworks:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li><strong>Biodiversity Integrity Index:</strong> Comprehensive ecosystem health scores</li>
            <li><strong>Functional Diversity Metrics:</strong> Ecosystem service provision capacity</li>
            <li><strong>Restoration Success Indicators:</strong> Quantified recovery trajectories</li>
            <li><strong>Blue Carbon Potential:</strong> Verified carbon sequestration rates</li>
            <li><strong>Threat Assessment Scores:</strong> Risk quantification and early warning</li>
          </ul>
        </section>

        {/* Applications */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{
            fontSize: '1.8rem',
            color: '#EFEFE3',
            marginBottom: '1.5rem',
            fontWeight: 600
          }}>
            Applications: Transforming Ocean Management
          </h2>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Conservation Performance Tracking
          </h3>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Marine Protected Areas (MPAs) cover 8% of oceans but often lack data to demonstrate effectiveness. Our monitoring systems provide:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li>Baseline biodiversity assessments before protection</li>
            <li>Real-time tracking of ecosystem recovery</li>
            <li>Spillover effect quantification to adjacent areas</li>
            <li>Adaptive management recommendations based on outcomes</li>
            <li>Standardized reporting for international commitments</li>
          </ul>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Blue Finance Innovation
          </h3>

          <h4 style={{ fontSize: '1.2rem', color: '#EFEFE3', marginTop: '1.5rem', marginBottom: '0.8rem' }}>
            Biodiversity Credits
          </h4>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            We enable the world's first science-based marine biodiversity credits by providing:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li>Quantified biodiversity uplift from conservation actions</li>
            <li>Permanence risk assessment and monitoring</li>
            <li>Additionality verification through counterfactual modeling</li>
            <li>Third-party auditable measurement protocols</li>
            <li>Blockchain-secured credit registry and trading</li>
          </ul>

          <h4 style={{ fontSize: '1.2rem', color: '#EFEFE3', marginTop: '1.5rem', marginBottom: '0.8rem' }}>
            Blue Bonds and Debt Swaps
          </h4>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Performance-based financial instruments tied to verified ocean health improvements:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li>Key Performance Indicators (KPIs) based on genomic metrics</li>
            <li>Independent verification through our monitoring network</li>
            <li>Risk assessment and pricing models</li>
            <li>Impact reporting for ESG compliance</li>
            <li>Sovereign debt restructuring linked to conservation outcomes</li>
          </ul>

          <h4 style={{ fontSize: '1.2rem', color: '#EFEFE3', marginTop: '1.5rem', marginBottom: '0.8rem' }}>
            Marine Genetic Resources
          </h4>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Sustainable bioprospecting with equitable benefit-sharing:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li>Discovery of novel enzymes for industrial applications</li>
            <li>Bioactive compounds for pharmaceutical development</li>
            <li>CRISPR systems for biotechnology applications</li>
            <li>Digital Sequence Information (DSI) management</li>
            <li>Revenue sharing with source nations and communities</li>
          </ul>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Climate Adaptation and Resilience
          </h3>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Understanding how marine ecosystems respond to climate change enables proactive management:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li>Early warning systems for coral bleaching and ecosystem collapse</li>
            <li>Identification of climate refugia and migration corridors</li>
            <li>Assisted evolution and restoration prioritization</li>
            <li>Blue carbon enhancement strategies</li>
            <li>Fisheries adaptation planning based on species distribution shifts</li>
          </ul>
        </section>

        {/* Technology Platform */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{
            fontSize: '1.8rem',
            color: '#EFEFE3',
            marginBottom: '1.5rem',
            fontWeight: 600
          }}>
            Technology Platform: Integrated Ocean Intelligence
          </h2>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Data Architecture
          </h3>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Our cloud-native platform processes and integrates diverse data streams:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li><strong>Genomic pipelines:</strong> Automated sequence processing, quality control, and taxonomic assignment</li>
            <li><strong>Environmental integration:</strong> Fusion with satellite imagery, ocean sensors, and climate models</li>
            <li><strong>Temporal analytics:</strong> Time-series analysis and trend detection</li>
            <li><strong>Spatial modeling:</strong> 3D ecosystem mapping and connectivity analysis</li>
            <li><strong>Predictive engines:</strong> Machine learning models for forecasting and scenario planning</li>
          </ul>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            User Interfaces
          </h3>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Tailored dashboards for different stakeholders:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li><strong>Conservation Managers:</strong> Real-time ecosystem monitoring, alert systems, adaptive management tools</li>
            <li><strong>Scientists:</strong> Research portals, data exploration, hypothesis testing environments</li>
            <li><strong>Investors:</strong> Performance metrics, risk assessments, impact reporting</li>
            <li><strong>Policymakers:</strong> Compliance tracking, scenario modeling, decision support systems</li>
            <li><strong>Communities:</strong> Local resource management tools, traditional knowledge integration</li>
          </ul>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            AI Capabilities
          </h3>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Natural language processing enables intuitive interaction with complex data:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li>Ask questions in plain language: "What species are most at risk in this area?"</li>
            <li>Generate automated reports and insights</li>
            <li>Identify anomalies and emerging threats</li>
            <li>Recommend management interventions</li>
            <li>Translate between scientific, financial, and policy languages</li>
          </ul>
        </section>

        {/* Impact and Scale */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{
            fontSize: '1.8rem',
            color: '#EFEFE3',
            marginBottom: '1.5rem',
            fontWeight: 600
          }}>
            Impact at Scale: The Path to Ocean Recovery
          </h2>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Immediate Benefits
          </h3>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li><strong>10x more biodiversity data</strong> from each monitoring effort</li>
            <li><strong>75% cost reduction</strong> compared to traditional surveys</li>
            <li><strong>Real-time insights</strong> instead of annual reports</li>
            <li><strong>Non-invasive monitoring</strong> protecting sensitive habitats</li>
            <li><strong>Standardized metrics</strong> enabling global comparison</li>
          </ul>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Medium-term Transformation (2-5 years)</h3>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li><strong>$10 billion</strong> in new conservation finance mobilized</li>
            <li><strong>1000+ MPAs</strong> with comprehensive monitoring systems</li>
            <li><strong>50+ nations</strong> implementing genomic ocean strategies</li>
            <li><strong>100+ blue bonds</strong> issued with performance verification</li>
            <li><strong>500+ species</strong> recovered through targeted interventions</li>
          </ul>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Long-term Vision (10+ years)
          </h3>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            A global ocean intelligence network enabling:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li>Predictive management preventing ecosystem collapse</li>
            <li>Mature biodiversity credit markets funding conservation at scale</li>
            <li>Climate-resilient ocean ecosystems supporting blue economies</li>
            <li>Equitable benefit-sharing from marine genetic resources</li>
            <li>Achievement of UN Sustainable Development Goal 14: Life Below Water</li>
          </ul>
        </section>

        {/* Business Model */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{
            fontSize: '1.8rem',
            color: '#EFEFE3',
            marginBottom: '1.5rem',
            fontWeight: 600
          }}>
            Business Model: Sustainable Value Creation
          </h2>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Revenue Streams
          </h3>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li><strong>Monitoring Services:</strong> Subscription-based ecosystem monitoring for MPAs, governments, and conservation organizations</li>
            <li><strong>Verification Services:</strong> Third-party verification for biodiversity credits, blue bonds, and impact investments</li>
            <li><strong>Data Analytics:</strong> Custom analysis and reporting for research institutions and policy makers</li>
            <li><strong>Platform Licensing:</strong> White-label solutions for national ocean monitoring programs</li>
            <li><strong>Bioprospecting Partnerships:</strong> Revenue sharing from marine genetic resource discoveries</li>
          </ul>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Market Opportunity
          </h3>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            The addressable market for ocean intelligence and conservation finance exceeds $50 billion annually:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li>Ocean monitoring and assessment: $5 billion</li>
            <li>Biodiversity credit markets: $10 billion (projected)</li>
            <li>Blue bonds and conservation finance: $20 billion</li>
            <li>Marine biotechnology: $6 billion</li>
            <li>Fisheries management and compliance: $10 billion</li>
          </ul>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Competitive Advantages
          </h3>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li><strong>First-mover advantage</strong> in genomic ocean monitoring</li>
            <li><strong>Proprietary AI models</strong> trained on global datasets</li>
            <li><strong>End-to-end platform</strong> from data collection to finance</li>
            <li><strong>Scientific credibility</strong> through peer-reviewed methods</li>
            <li><strong>Network effects</strong> as more users contribute data</li>
          </ul>
        </section>

        {/* Team and Partners */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{
            fontSize: '1.8rem',
            color: '#EFEFE3',
            marginBottom: '1.5rem',
            fontWeight: 600
          }}>
            Team and Partnerships
          </h2>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Core Expertise
          </h3>
          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            Our team combines world-class expertise across critical domains:
          </p>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li><strong>Marine genomics and bioinformatics:</strong> Leaders in eDNA and metagenomics application</li>
            <li><strong>Artificial intelligence and data science:</strong> Experts in machine learning and knowledge graphs</li>
            <li><strong>Ocean science and conservation:</strong> Decades of field experience and ecosystem understanding</li>
            <li><strong>Conservation finance:</strong> Pioneers in biodiversity credits and impact investment</li>
            <li><strong>Technology and platform development:</strong> Scalable cloud infrastructure and user experience</li>
          </ul>

          <h3 style={{ fontSize: '1.4rem', color: '#0888D0', marginTop: '2rem', marginBottom: '1rem' }}>
            Strategic Partnerships
          </h3>
          <ul style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            <li><strong>Research institutions:</strong> Collaboration with leading oceanographic institutes</li>
            <li><strong>Conservation organizations:</strong> Implementation partners for field deployment</li>
            <li><strong>Financial institutions:</strong> Development of new blue finance instruments</li>
            <li><strong>Technology providers:</strong> Cloud computing and AI infrastructure</li>
            <li><strong>Governments and multilaterals:</strong> Policy alignment and program implementation</li>
          </ul>
        </section>

        {/* Call to Action */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{
            fontSize: '1.8rem',
            color: '#EFEFE3',
            marginBottom: '1.5rem',
            fontWeight: 600
          }}>
            Join the Ocean Intelligence Revolution
          </h2>

          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '1rem' }}>
            The ocean crisis demands immediate action at unprecedented scale. Ocean BioMetrics Ltd. provides the tools, metrics, and mechanisms to transform how we understand, value, and protect marine ecosystems. By making the invisible visible and the complex clear, we enable a future where ocean conservation is not just morally imperative but financially sustainable.
          </p>

          <p style={{ color: '#D5D6CC', lineHeight: '1.8', marginBottom: '2rem' }}>
            Whether you're a conservation organization seeking better monitoring tools, an investor looking for verified impact opportunities, a government implementing ocean strategies, or a company committed to ocean stewardship, Ocean BioMetrics Ltd. is your partner in creating a thriving blue economy that works for people and planet.
          </p>

          <div style={{
            background: 'linear-gradient(135deg, rgba(8, 136, 208, 0.1), rgba(8, 136, 208, 0.05))',
            border: '1px solid rgba(8, 136, 208, 0.3)',
            borderRadius: '15px',
            padding: '2rem',
            textAlign: 'center',
            marginTop: '3rem'
          }}>
            <h3 style={{ color: '#0888D0', marginBottom: '1rem', fontSize: '1.5rem' }}>
              Ready to Transform Ocean Conservation?
            </h3>
            <p style={{ color: '#D5D6CC', marginBottom: '1.5rem' }}>
              Contact us to explore how Ocean BioMetrics Ltd. can support your ocean conservation and sustainability goals.
            </p>
            <a
              href="/#contact"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem 2.5rem',
                background: 'linear-gradient(135deg, rgba(24, 195, 186, 0.15) 0%, rgba(24, 195, 186, 0.25) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(24, 195, 186, 0.3)',
                borderRadius: '50px',
                color: '#18C3BA',
                textDecoration: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                fontFamily: "'Montserrat', sans-serif",
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 25px rgba(24, 195, 186, 0.15)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(24, 195, 186, 0.25) 0%, rgba(24, 195, 186, 0.35) 100%)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 35px rgba(24, 195, 186, 0.25)';
                e.currentTarget.style.borderColor = 'rgba(24, 195, 186, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(24, 195, 186, 0.15) 0%, rgba(24, 195, 186, 0.25) 100%)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(24, 195, 186, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(24, 195, 186, 0.3)';
              }}
            >
              <span>Get in Touch</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                style={{
                  transition: 'transform 0.3s ease'
                }}
              >
                <path
                  d="M7 17L17 7M17 7H7M17 7V17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>
        </section>

        {/* Footer Note */}
        <div style={{
          paddingTop: '2rem',
          marginTop: '2rem',
          textAlign: 'center'
        }}>
          <p style={{ color: '#888', fontSize: '0.9rem' }}>
            © 2025 Ocean BioMetrics Ltd. All rights reserved.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default WhitepaperPage;